import Controllers from "@/controllers/index";
import store from "@/stores/store.js";
import { SET } from "@/stores/CommumActionTypes.js";

// #region Factories

/**
 * @summary Função que cria um objeto de company vazio.
 * @returns Objeto de company.
 */
const EmptyCompanyFactory = function()
{
	 return {
		 id:      0,
		 name:    "",
		 cnpj:    "",
		 type:    0,
		 remarks: "",
		 emails:  [],
	 };
};

/**
 * @summary Cria o state de Company.
 * @returns State de Company.
 */
const EmptyCompanyStateFactory = function()
{
	 return {
		 company:   EmptyCompanyFactory(),
		 companies: [],
	 };
};

/**
 * @summary Factory de company passando como parametro uma empresa
 * @param company Objeto company com as propriedades de company que precisam atualizar.
 * @returns Retorna um novo company com as propriedades atualizadas.
 */
const CompanyFactory = function(company = EmptyCompanyFactory())
{
	 return {
		 id:      company.id,
		 name:    company.name,
		 cnpj:    company.cnpj,
		 type:    company.type,
		 remarks: company.remarks,
		 emails:  company.emails,
	 };
};

/**
 * @summary Cria um model para uma empresa.
 * @param company View de company com as propriedades de company que precisam atualizar.
 * @returns Retorna um novo company com as propriedades atualizadas.
 */
const CompanyModelFactory = function(company = EmptyCompanyFactory())
{
	 return {
		id:      company.id,
		name:    company.name,
		cnpj:    company.cnpj,
		type:    company.type,
		remarks: company.remarks,
		logo:    company.logo,
	 };
};

/**
 *
 * @param parkId - Id real do parque que está na base de dados.
 * @param turbineName - Nome da turbina.
 * @param id
 * @returns Turbina mock para home.
 */
const FactoryWindTurbineInTreeForDemo = function(parkId = 0, turbineName = "", id = 0)
{
	return {
		companiesId:                           20,
		connectedProtocolTrendSignalSettingId: 4772,
		id:                                    id,
		latitude:                              -27.508386,
		longitude:                             -48.512321,
		maxWindSpeed:                          25,
		minWindSpeed:                          4,
		nacellePositionTrendSignalSettingId:   0,
		name:                                  turbineName,
		parkId:                                parkId,
		powerTrendSignalSettingId:             0,
		ratedPower:                            2.1,
		windDirectionTrendSignalSettingId:     0,
		windSpeedTrendSignalSettingId:         0,
	};
};

/**
 * @summary Função que cria um objeto de generatingUnits vazio.
 * @returns Objeto de company.
 */

const EmptyCompanyGeneratingUnitsFactory = function()
{
	return {
		id:       "",
		name:     "",
		parentId: 0,
	};
};

const CompanyGeneratingUnitsFactory = function(generatingUnits = EmptyCompanyGeneratingUnitsFactory())
{
	return {
		id:       generatingUnits.id,
		name:     generatingUnits.name,
		parentId: generatingUnits.parentId,
	};
};

// #endregion

// #region API Calls

/**
* @summary Busca uma empresa atrávés do seu Id.
* @param  companyId - Id da empresa;
* @returns Uma empresa.
*/
const GetCompany = async function(companyId = 0)
{
	const resp = await Controllers.Companies.GetCompany(companyId);
	if (resp.status === 204) return null;
	const company = CompanyFactory(resp.data);
	store.dispatch(`CompanyModule/${SET}`, company);
	return company;
};

/**
 * @summary Busca a árvore de monitoramento de uma empresa.
 * @param companyId Id da empresa;
 * @returns {object} Uma árvore de monitoramento.
 */
const GetWindMonitoringElementTree = async function(companyId = 0)
{
	const resp = await Controllers.Companies.GetWindMonitoringElementTree(companyId);
	if (resp.status !== 200) return null;

	return resp.data;
};

/**
 * @summary Atualiza uma empresa.
 * @param id - Id da empresa;
 * @param companyModel - Model de atualização da empresa.
 * @returns Empresa atualizada.
 */
const UpdateCompany = async function(id = 0, updateCompany = CompanyModelFactory())
{
	const resp = await Controllers.Companies.PutCompany(id, updateCompany);
	return CompanyFactory(resp.data);
};

/**
* @summary Busca todas as empresas do sistema.
* @returns {[CompanyFactory]} - Lista de empresas.
*/
const GetCompanies = async function()
{
	const resp = await Controllers.Companies.GetCompanies();
	if (resp.status === 204) return [];

	const companies = resp.data.map((c) => CompanyFactory(c));
	return companies;
};

// #endregion

// #region Methods

/**
 * @summary Cria turbinas mock para a tree de monitoringElements da home para o parque 1 e para o parque 2.
 * @returns Array de todas as Turbinas dos parques.
 */
const CreateWindTurbinesInTreeForDemo = function()
{
	const windTurbines = [];

	// Id da base do parque 1;
	const parkId1 = 1894;

	// Id da base do parque 2;
	const parkId2 = 1895;

	for (let i = 0; i < 23; i++)
	{
		// Nomes das turbinas. (Já existe 1 e 2 na base, por isso os nomes começam a partir do 3)
		const turbineNameNumber = i + 3;
		const turbineName1 = turbineNameNumber < 10 ? `FM01-WTG0${turbineNameNumber}` : `FM01-WTG${turbineNameNumber}`;
		const turbineName2 = turbineNameNumber < 10 ? `FM02-WTG0${turbineNameNumber}` : `FM02-WTG${turbineNameNumber}`;

		// Id das turbinas mock negativos para não confundir com ids reais da base.
		const turbineId1 = -1 * (i + 1);
		const turbineId2 = -23 * (i + 1);

		// Cria os objetos das turbinas de cada parque.
		const windTurbinePark1 = FactoryWindTurbineInTreeForDemo(parkId1, turbineName1, turbineId1);
		const windTurbinePark2 = FactoryWindTurbineInTreeForDemo(parkId2, turbineName2, turbineId2);

		// Adiciona as turbinas
		windTurbines.push(windTurbinePark1, windTurbinePark2);
	}
	return windTurbines;
};

/**
* @summary Busca todas as unidades geradoras de uma empresa.
* @returns {[CompanyGeneratingUnitsFactory]} - Lista de unidades geradoras.
*/
const GetCompanyGeneratingUnits = async function(companyId)
{
	const resp = await Controllers.Companies.GetCompanyGeneratingUnits(companyId);
	if (resp.status === 204) return [];

	const generatingUnits = resp.data.map((data) => CompanyGeneratingUnitsFactory(data));

	store.dispatch(`Trend/TrendMenu/TrendAssetSelector/${SET}`, generatingUnits);
	return generatingUnits;
};

/**
* @summary Busca todos os emails de uma empresa.
* @returns Lista de emails da empresa.
*/
const GetCompanyEmails = async function(companyId)
{
	const resp = await Controllers.Companies.GetCompanyEmails(companyId);
	if (resp.status === 204) return [];

	return resp.data;
};

// #endregion

export default {
	GetCompanies,
	GetWindMonitoringElementTree,
	GetCompany,
	UpdateCompany,
	EmptyCompanyFactory,
	EmptyCompanyStateFactory,
	CompanyFactory,
	CreateWindTurbinesInTreeForDemo,
	EmptyCompanyGeneratingUnitsFactory,
	GetCompanyGeneratingUnits,
	GetCompanyEmails,
};
